import { JSONSchema7 } from 'json-schema';
import FaviconJS from '@ryanrutkin/favicon.js';
import { dataURItoBlob } from '../../utils/dataURItoBlob';
import { PngIcoConverter } from '../../utils/favicon/png2icojs';
import { validateFormData } from '../../utils/jsonSchemaValidator';

export type ValidationState = {
  canSave: boolean;
  requiredDetails: string[];
};

export const normalizeType = (t: string) => String(t || '').toUpperCase();

export const formatSchemaTypeTitle = (schemaType: string): string => {
  const isSchemaType = schemaType.indexOf('QUOTE_EXPERIENCE_') > -1;

  const formattedName = schemaType
    .substring(isSchemaType ? 'QUOTE_EXPERIENCE_'.length : 0)
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return `${formattedName}${isSchemaType ? ` (Schema)` : ''}`
};


export type ValidatableSchema = { schema_type: string; schema_type_version?: number; schema: JSONSchema7 };

export const buildSchemaValidationState = (
  quoteSchemas: ValidatableSchema[],
  formDataByType: Record<string, any>,
  passedState?: ValidationState
): ValidationState => {
  const state: ValidationState = passedState || {
    canSave: true,
    requiredDetails: [],
  };

  quoteSchemas.forEach(({ schema_type, schema }) => {
    const normalizedSchemaType = normalizeType(schema_type);
    const title = schema.title || formatSchemaTypeTitle(schema_type);
    const validation = validateFormData(schema, formDataByType[normalizedSchemaType], title);

    state.canSave = state.canSave && validation.isValid;
    state.requiredDetails.push(...validation.messages);
  });

  return state;
};

export function blobToDataURI(blob: Blob): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(blob);
  });
}

export type FaviconAssets = {
  pngBlob: Blob;
  pngDataUri: string;
  icoBlob: Blob;
  icoDataUri: string | ArrayBuffer | null;
};

export async function buildFaviconAssets(
  image: HTMLImageElement
): Promise<FaviconAssets> {
  const favicon = new FaviconJS(image);

  const pngUri = favicon.png(96);
  const pngBlob = dataURItoBlob(pngUri);

  const converter = new PngIcoConverter();
  const icoBlob = await converter.convertToBlobAsync([{ png: pngBlob }]);

  const icoDataUri = await blobToDataURI(icoBlob);

  return {
    pngBlob,
    pngDataUri: pngUri,
    icoBlob,
    icoDataUri,
  };
}
